import {
  claimRestStorage,
  type Drawing,
  type TakeoffSummary,
} from '@/lib/data';
import type { MarkupSummary } from '@/lib/markup-designer';
import {
  createContext,
  useContext,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';

interface TakeoffSummaryContextType {
  takeoffSummary: TakeoffSummary;
  setTakeoffSummary: Dispatch<SetStateAction<TakeoffSummary>>;
}

// Contexts
const TakeoffSummaryContext = createContext<
  TakeoffSummaryContextType | undefined
>(undefined);
TakeoffSummaryContext.displayName = 'TakeoffSummaryContext';

// Initial State
const takeoffSummary: TakeoffSummary = {
  id: '',
  claim_id: '',
  drawings: [],
};
const initialState = takeoffSummary;

// Provider
const TakeoffSummaryProvider = (props: any) => {
  const [takeoffSummary, setTakeoffSummary] =
    useState<TakeoffSummary>(initialState);
  const value = { takeoffSummary, setTakeoffSummary };

  return <TakeoffSummaryContext.Provider {...props} value={value} />;
};

// Hook
function useTakeoffSummaryClient() {
  const context = useContext(TakeoffSummaryContext);

  if (!context) {
    throw new Error(
      `useTakeoffSummaryClient must be used within the TakeoffSummaryProvider`,
    );
  }

  const { takeoffSummary, setTakeoffSummary } = context;
  const { saveDrawing } = claimRestStorage;

  const updateProgress = (data: TakeoffSummary) => {
    setTakeoffSummary({ ...data });
  };

  const save = async (data: MarkupSummary) => {
    const updatedDrawing: Drawing = {
      ...takeoffSummary.drawings[0],
      drawing: data.drawing,
      markers: data.features,
      deleted: data.deleted,
    };

    await saveDrawing({ drawing: updatedDrawing });
    updateProgress({ ...takeoffSummary, drawings: [updatedDrawing] });

    return Promise.resolve();
  };

  const reset = () => {
    updateProgress(initialState);
  };

  return {
    takeoffSummary,
    updateProgress,
    save,
    reset,
  };
}

// Combo export
export { TakeoffSummaryProvider, useTakeoffSummaryClient };
